import { ENV } from '../config/env';
import { supabase } from '../config/supabaseClient';
import { errorHandlingService } from './errorHandlingService';

const API_BASE_URL = `${ENV.API_URL}/api/admin`;

const getToken = async (): Promise<string> => {
  try {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token || '';
  } catch (e) {
    console.error('adminService: error getting Supabase token', e);
    return '';
  }
};

// Compliance & Privacy Ops APIs
export const complianceService = {
  // Consent settings
  async getConsentSettings(programId: string): Promise<any | null> {
    requireString(programId, 'programId');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/compliance/${encodeURIComponent(programId)}/consent`, {
        method: 'GET', headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });
      return handleResponse(res);
    } catch (e) {
      const error = e instanceof Error ? e : new Error(String(e));
      errorHandlingService.logError(error, 'complianceService.getConsentSettings');
      throw error;
    }
  },
  async updateConsentSettings(programId: string, payload: { purposes: Record<string, boolean>; regions?: Record<string, any>; version?: string }): Promise<any> {
    requireString(programId, 'programId');
    if (!payload || typeof payload !== 'object') throw new Error('payload is required');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/compliance/${encodeURIComponent(programId)}/consent`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': token ? `Bearer ${token}` : '' }, body: JSON.stringify(payload)
      });
      return handleResponse(res);
    } catch (e) {
      const error = e instanceof Error ? e : new Error(String(e));
      errorHandlingService.logError(error, 'complianceService.updateConsentSettings');
      throw error;
    }
  },

  // Data Subject Requests (DSR)
  async submitDSR(payload: { subject_type: 'affiliate' | 'customer'; subject_id?: string; email?: string; type: 'access' | 'deletion' | 'rectification'; details?: string }): Promise<any> {
    if (!payload || typeof payload !== 'object') throw new Error('payload is required');
    requireString(payload.type, 'type');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/compliance/dsr`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': token ? `Bearer ${token}` : '' }, body: JSON.stringify(payload)
      });
      return handleResponse(res);
    } catch (e) {
      const error = e instanceof Error ? e : new Error(String(e));
      errorHandlingService.logError(error, 'complianceService.submitDSR');
      throw error;
    }
  },
  async getDSRRequests(filters: Partial<{ status: 'open' | 'in_progress' | 'closed'; type: 'access' | 'deletion' | 'rectification'; email: string; from: string; to: string; limit: number }> = {}): Promise<any[]> {
    try {
      const token = await getToken();
      const url = new URL(`${API_BASE_URL}/compliance/dsr`);
      Object.entries(filters).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v)); });
      const res = await fetch(url.toString(), { method: 'GET', headers: { 'Authorization': token ? `Bearer ${token}` : '' } });
      return handleResponse(res);
    } catch (e) {
      const error = e instanceof Error ? e : new Error(String(e));
      errorHandlingService.logError(error, 'complianceService.getDSRRequests');
      throw error;
    }
  },
  async updateDSRStatus(id: string, status: 'open' | 'in_progress' | 'closed', notes?: string): Promise<any> {
    requireString(id, 'id');
    requireString(status, 'status');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/compliance/dsr/${encodeURIComponent(id)}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', 'Authorization': token ? `Bearer ${token}` : '' }, body: JSON.stringify({ status, notes })
      });
      return handleResponse(res);
    } catch (e) {
      const error = e instanceof Error ? e : new Error(String(e));
      errorHandlingService.logError(error, 'complianceService.updateDSRStatus');
      throw error;
    }
  },

  // Data retention policy
  async getRetentionPolicy(programId: string): Promise<any | null> {
    requireString(programId, 'programId');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/compliance/${encodeURIComponent(programId)}/retention`, {
        method: 'GET', headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });
      return handleResponse(res);
    } catch (e) {
      const error = e instanceof Error ? e : new Error(String(e));
      errorHandlingService.logError(error, 'complianceService.getRetentionPolicy');
      throw error;
    }
  },
  async updateRetentionPolicy(programId: string, payload: { durations: Record<string, number>; purge_enabled?: boolean }): Promise<any> {
    requireString(programId, 'programId');
    if (!payload || typeof payload !== 'object') throw new Error('payload is required');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/compliance/${encodeURIComponent(programId)}/retention`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': token ? `Bearer ${token}` : '' }, body: JSON.stringify(payload)
      });
      return handleResponse(res);
    } catch (e) {
      const error = e instanceof Error ? e : new Error(String(e));
      errorHandlingService.logError(error, 'complianceService.updateRetentionPolicy');
      throw error;
    }
  },

  // Audit trail (admin-only, premium-gated)
  async getAuditTrail(filters: Partial<{ actor: string; action: string; resource: string; from: string; to: string; limit: number }> = {}): Promise<any[]> {
    try {
      const token = await getToken();
      const url = new URL(`${API_BASE_URL}/compliance/audit-trail`);
      Object.entries(filters).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v)); });
      const res = await fetch(url.toString(), { method: 'GET', headers: { 'Authorization': token ? `Bearer ${token}` : '' } });
      return handleResponse(res);
    } catch (e) {
      const error = e instanceof Error ? e : new Error(String(e));
      errorHandlingService.logError(error, 'complianceService.getAuditTrail');
      throw error;
    }
  }
};

async function handleResponse<T>(response: Response): Promise<T> {
  const text = await response.text();
  let data: any = null;
  try { data = text ? JSON.parse(text) : null; } catch { /* non-JSON */ }

  if (!response.ok) {
    const message = (data && (data.message || data.error)) || text || 'Unknown error';
    errorHandlingService.logError(new Error(message), `adminService [${response.status}]`);
    throw new Error(message);
  }

  return (data as T) ?? ({} as T);
}

const requireString = (v: any, name: string) => {
  if (!v || typeof v !== 'string') throw new Error(`${name} is required`);
};

export const adminService = {
  // Attribution settings
  async getAttributionSettings(programId: string): Promise<any | null> {
    requireString(programId, 'programId');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/attribution/settings/${encodeURIComponent(programId)}`, {
        method: 'GET',
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });
      return handleResponse(res);
    } catch (e) {
      const error = e instanceof Error ? e : new Error(String(e));
      errorHandlingService.logError(error, 'adminService.getAttributionSettings');
      throw error;
    }
  },
  async updateAttributionSettings(programId: string, updates: Partial<{ model: 'first_touch' | 'last_touch' | 'referrer'; lookback_days: number; lifetime_enabled: boolean; recurring_enabled: boolean; }>): Promise<any> {
    requireString(programId, 'programId');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/attribution/settings/${encodeURIComponent(programId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': token ? `Bearer ${token}` : '' },
        body: JSON.stringify(updates || {})
      });
      return handleResponse(res);
    } catch (e) {
      const error = e instanceof Error ? e : new Error(String(e));
      errorHandlingService.logError(error, 'adminService.updateAttributionSettings');
      throw error;
    }
  },

  // Webhook endpoints
  async listWebhooks(): Promise<any[]> {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/webhooks`, { method: 'GET', headers: { 'Authorization': token ? `Bearer ${token}` : '' } });
      return handleResponse(res);
    } catch (e) {
      const error = e instanceof Error ? e : new Error(String(e));
      errorHandlingService.logError(error, 'adminService.listWebhooks');
      throw error;
    }
  },
  async createWebhook(params: { name: string; url: string; secret?: string; active?: boolean }): Promise<any> {
    requireString(params?.name, 'name');
    requireString(params?.url, 'url');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/webhooks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': token ? `Bearer ${token}` : '' },
        body: JSON.stringify(params)
      });
      return handleResponse(res);
    } catch (e) {
      const error = e instanceof Error ? e : new Error(String(e));
      errorHandlingService.logError(error, 'adminService.createWebhook');
      throw error;
    }
  },
  async updateWebhook(id: string, updates: Partial<{ name: string; url: string; secret: string; active: boolean }>): Promise<any> {
    requireString(id, 'id');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/webhooks/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': token ? `Bearer ${token}` : '' },
        body: JSON.stringify(updates || {})
      });
      return handleResponse(res);
    } catch (e) {
      const error = e instanceof Error ? e : new Error(String(e));
      errorHandlingService.logError(error, 'adminService.updateWebhook');
      throw error;
    }
  },
  async deleteWebhook(id: string): Promise<{ success: boolean }> {
    requireString(id, 'id');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/webhooks/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });
      return handleResponse(res);
    } catch (e) {
      const error = e instanceof Error ? e : new Error(String(e));
      errorHandlingService.logError(error, 'adminService.deleteWebhook');
      throw error;
    }
  },

  // Subscription commissions
  async upsertSubscriptionCommission(params: { subscription_id: string; affiliate_id: string; rate?: number; type?: 'percent' | 'fixed'; active?: boolean }): Promise<any> {
    requireString(params?.subscription_id, 'subscription_id');
    requireString(params?.affiliate_id, 'affiliate_id');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/subscription-commissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': token ? `Bearer ${token}` : '' },
        body: JSON.stringify(params)
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'adminService.upsertSubscriptionCommission');
      throw e;
    }
  },
  async getSubscriptionCommission(subscriptionId: string): Promise<any | null> {
    requireString(subscriptionId, 'subscriptionId');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/subscription-commissions/${encodeURIComponent(subscriptionId)}`, {
        method: 'GET',
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'adminService.getSubscriptionCommission');
      throw e;
    }
  },
  async deleteSubscriptionCommission(subscriptionId: string): Promise<{ success: boolean }> {
    requireString(subscriptionId, 'subscriptionId');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/subscription-commissions/${encodeURIComponent(subscriptionId)}`, {
        method: 'DELETE',
        headers: { 'Authorization': token ? `Bearer ${token}` : '' }
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'adminService.deleteSubscriptionCommission');
      throw e;
    }
  },

  // Commissions
  async listCommissions(filters: Partial<{ affiliate_id: string; status: 'pending' | 'approved' | 'paid' | 'void'; from: string; to: string; limit: number }> = {}): Promise<any[]> {
    try {
      const token = await getToken();
      const url = new URL(`${API_BASE_URL}/commissions`);
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
      });
      const res = await fetch(url.toString(), { method: 'GET', headers: { 'Authorization': token ? `Bearer ${token}` : '' } });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'adminService.listCommissions');
      throw e;
    }
  },
  async updateCommissionStatus(id: string, status: 'pending' | 'approved' | 'paid' | 'void'): Promise<any> {
    requireString(id, 'id');
    requireString(status, 'status');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/commissions/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': token ? `Bearer ${token}` : '' },
        body: JSON.stringify({ status })
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'adminService.updateCommissionStatus');
      throw e;
    }
  }
  ,
  // Integrations: Slack
  async getSlackConfig(): Promise<any | null> {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/integrations/slack`, { method: 'GET', headers: { 'Authorization': token ? `Bearer ${token}` : '' } });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'adminService.getSlackConfig');
      throw e;
    }
  },
  async saveSlackConfig(params: { workspace?: string; channel?: string; enabled?: boolean; event_filters?: string[] }): Promise<{ success: boolean }> {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/integrations/slack`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': token ? `Bearer ${token}` : '' },
        body: JSON.stringify(params || {})
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'adminService.saveSlackConfig');
      throw e;
    }
  },
  async sendSlackTest(): Promise<{ delivered: boolean }> {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/integrations/slack/test`, { method: 'POST', headers: { 'Authorization': token ? `Bearer ${token}` : '' } });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'adminService.sendSlackTest');
      throw e;
    }
  },

  // Integrations: Email
  async getEmailConfig(): Promise<any | null> {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/integrations/email`, { method: 'GET', headers: { 'Authorization': token ? `Bearer ${token}` : '' } });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'adminService.getEmailConfig');
      throw e;
    }
  },
  async saveEmailConfig(params: { sender?: string; provider?: 'smtp' | 'resend' | 'sendgrid'; enabled?: boolean; templates?: Record<string, string> }): Promise<{ success: boolean }> {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/integrations/email`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': token ? `Bearer ${token}` : '' },
        body: JSON.stringify(params || {})
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'adminService.saveEmailConfig');
      throw e;
    }
  },
  async sendEmailTest(): Promise<{ delivered: boolean }> {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/integrations/email/test`, { method: 'POST', headers: { 'Authorization': token ? `Bearer ${token}` : '' } });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'adminService.sendEmailTest');
      throw e;
    }
  },

  // Integrations: Destinations (Segment/CRM)
  async listDestinations(): Promise<any[]> {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/integrations/destinations`, { method: 'GET', headers: { 'Authorization': token ? `Bearer ${token}` : '' } });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'adminService.listDestinations');
      throw e;
    }
  },
  async upsertDestination(params: { id?: string; type: 'segment' | 'hubspot' | 'salesforce'; config: Record<string, any>; enabled?: boolean; event_filters?: string[] }): Promise<any> {
    if (!params || typeof params !== 'object') throw new Error('params is required');
    if (!params.type) throw new Error('type is required');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/integrations/destinations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': token ? `Bearer ${token}` : '' },
        body: JSON.stringify(params)
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'adminService.upsertDestination');
      throw e;
    }
  },
  async deleteDestination(id: string): Promise<{ success: boolean }> {
    requireString(id, 'id');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/integrations/destinations/${encodeURIComponent(id)}`, { method: 'DELETE', headers: { 'Authorization': token ? `Bearer ${token}` : '' } });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'adminService.deleteDestination');
      throw e;
    }
  }
};

// Creatives & Coupons APIs
export const creativesService = {
  // Banners
  async listBanners(programId: string): Promise<any[]> {
    requireString(programId, 'programId');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/creatives/${encodeURIComponent(programId)}/banners`, { method: 'GET', headers: { 'Authorization': token ? `Bearer ${token}` : '' } });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'creativesService.listBanners');
      throw e;
    }
  },
  async upsertBanner(programId: string, payload: { id?: string; name: string; image_url: string; target_url?: string; active?: boolean; tags?: string[] }): Promise<any> {
    requireString(programId, 'programId');
    requireString(payload?.name, 'name');
    requireString(payload?.image_url, 'image_url');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/creatives/${encodeURIComponent(programId)}/banners`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': token ? `Bearer ${token}` : '' }, body: JSON.stringify(payload || {})
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'creativesService.upsertBanner');
      throw e;
    }
  },
  async deleteBanner(programId: string, id: string): Promise<{ success: boolean }> {
    requireString(programId, 'programId');
    requireString(id, 'id');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/creatives/${encodeURIComponent(programId)}/banners/${encodeURIComponent(id)}`, { method: 'DELETE', headers: { 'Authorization': token ? `Bearer ${token}` : '' } });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'creativesService.deleteBanner');
      throw e;
    }
  },

  // Feeds (product/data feeds for affiliates)
  async listFeeds(programId: string): Promise<any[]> {
    requireString(programId, 'programId');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/creatives/${encodeURIComponent(programId)}/feeds`, { method: 'GET', headers: { 'Authorization': token ? `Bearer ${token}` : '' } });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'creativesService.listFeeds');
      throw e;
    }
  },
  async upsertFeed(programId: string, payload: { id?: string; name: string; url: string; format?: 'csv' | 'json' | 'xml'; active?: boolean }): Promise<any> {
    requireString(programId, 'programId');
    requireString(payload?.name, 'name');
    requireString(payload?.url, 'url');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/creatives/${encodeURIComponent(programId)}/feeds`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': token ? `Bearer ${token}` : '' }, body: JSON.stringify(payload || {})
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'creativesService.upsertFeed');
      throw e;
    }
  },
  async deleteFeed(programId: string, id: string): Promise<{ success: boolean }> {
    requireString(programId, 'programId');
    requireString(id, 'id');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/creatives/${encodeURIComponent(programId)}/feeds/${encodeURIComponent(id)}`, { method: 'DELETE', headers: { 'Authorization': token ? `Bearer ${token}` : '' } });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'creativesService.deleteFeed');
      throw e;
    }
  },

  // Link builder
  async generateTrackingLink(programId: string, params: { base_url: string; affiliate_id?: string; campaign?: string; coupon?: string; utm?: Record<string, string> }): Promise<{ url: string }> {
    requireString(programId, 'programId');
    requireString(params?.base_url, 'base_url');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/creatives/${encodeURIComponent(programId)}/link-builder`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': token ? `Bearer ${token}` : '' }, body: JSON.stringify(params || {})
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'creativesService.generateTrackingLink');
      throw e;
    }
  },

  // Coupons
  async listCoupons(programId: string): Promise<any[]> {
    requireString(programId, 'programId');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/coupons/${encodeURIComponent(programId)}`, { method: 'GET', headers: { 'Authorization': token ? `Bearer ${token}` : '' } });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'creativesService.listCoupons');
      throw e;
    }
  },
  async upsertCoupon(programId: string, payload: { id?: string; code: string; description?: string; discount_type?: 'percent' | 'amount'; discount_value?: number; active?: boolean; starts_at?: string; ends_at?: string }): Promise<any> {
    requireString(programId, 'programId');
    requireString(payload?.code, 'code');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/coupons/${encodeURIComponent(programId)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': token ? `Bearer ${token}` : '' }, body: JSON.stringify(payload || {})
      });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'creativesService.upsertCoupon');
      throw e;
    }
  },
  async deleteCoupon(programId: string, id: string): Promise<{ success: boolean }> {
    requireString(programId, 'programId');
    requireString(id, 'id');
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE_URL}/coupons/${encodeURIComponent(programId)}/${encodeURIComponent(id)}`, { method: 'DELETE', headers: { 'Authorization': token ? `Bearer ${token}` : '' } });
      return handleResponse(res);
    } catch (e) {
      errorHandlingService.logError(e instanceof Error ? e : String(e), 'creativesService.deleteCoupon');
      throw e;
    }
  },
};
